import React, { useRef, useState, useCallback, useEffect } from 'react';
import { CELL_SIZE, CELL_GAP } from '../utils/constants';

function Block({ 
  block, 
  onMove, 
  getMoveRange, 
  isComplete,
  children 
}) {
  const blockRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [currentPos, setCurrentPos] = useState({ x: block.x, y: block.y });
  /** Latest grid position during drag; state can lag one frame behind pointer on fast release. */
  const currentPosRef = useRef({ x: block.x, y: block.y });
  const moveRangeRef = useRef(null);
  
  useEffect(() => {
    const next = { x: block.x, y: block.y };
    currentPosRef.current = next;
    setCurrentPos(next);
  }, [block.x, block.y]);
  
  const getPixelPosition = useCallback((gridX, gridY) => ({
    left: gridX * (CELL_SIZE + CELL_GAP),
    top: gridY * (CELL_SIZE + CELL_GAP),
  }), []);
  
  const getGridPosition = useCallback((pixelX, pixelY) => {
    const cellUnit = CELL_SIZE + CELL_GAP;
    return {
      x: Math.round(pixelX / cellUnit),
      y: Math.round(pixelY / cellUnit),
    };
  }, []);
  
  const handleDragStart = useCallback((clientX, clientY) => {
    if (isComplete) return;
    
    const rect = blockRef.current.getBoundingClientRect();
    const parentRect = blockRef.current.parentElement.getBoundingClientRect();
    
    setDragOffset({
      x: clientX - rect.left,
      y: clientY - rect.top,
    });
    
    moveRangeRef.current = getMoveRange(block.id);
    const start = { x: block.x, y: block.y };
    currentPosRef.current = start;
    setCurrentPos(start);
    setIsDragging(true);
  }, [block.id, block.x, block.y, getMoveRange, isComplete]);
  
  const handleDragMove = useCallback((clientX, clientY) => {
    if (!isDragging || !blockRef.current) return;
    
    const parentRect = blockRef.current.parentElement.getBoundingClientRect();
    const range = moveRangeRef.current;
    
    let newPixelX = clientX - parentRect.left - dragOffset.x;
    let newPixelY = clientY - parentRect.top - dragOffset.y;
    
    const gridPos = getGridPosition(newPixelX, newPixelY);
    
    let clampedX = Math.max(range.minX, Math.min(range.maxX, gridPos.x));
    let clampedY = Math.max(range.minY, Math.min(range.maxY, gridPos.y));
    
    if (block.orientation === 'H') {
      clampedY = block.y;
    } else {
      clampedX = block.x;
    }
    
    const next = { x: clampedX, y: clampedY };
    currentPosRef.current = next;
    setCurrentPos(next);
  }, [isDragging, dragOffset, block.orientation, block.x, block.y, getGridPosition]);
  
  const handleDragEnd = useCallback(() => {
    if (!isDragging) return;
    
    setIsDragging(false);
    const pos = currentPosRef.current;
    if (pos.x !== block.x || pos.y !== block.y) {
      onMove(block.id, pos.x, pos.y);
    }
  }, [isDragging, block.id, block.x, block.y, onMove]);
  
  useEffect(() => {
    if (!isDragging) return;
    
    const handleMouseMove = (e) => {
      e.preventDefault();
      handleDragMove(e.clientX, e.clientY);
    };
    
    const handleMouseUp = () => {
      handleDragEnd();
    };
    
    const handleTouchMove = (e) => {
      e.preventDefault();
      const touch = e.touches[0];
      handleDragMove(touch.clientX, touch.clientY);
    };
    
    const handleTouchEnd = () => {
      handleDragEnd();
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);
  
  const pixelPos = getPixelPosition(currentPos.x, currentPos.y);
  const width = block.width * CELL_SIZE + (block.width - 1) * CELL_GAP;
  const height = block.height * CELL_SIZE + (block.height - 1) * CELL_GAP;
  
  const isEscaping = isComplete && block.isPrisoner;
  
  const classNames = [
    'unblock-block',
    block.isPrisoner ? 'unblock-block--prisoner' : '',
    isDragging ? 'unblock-block--dragging' : '',
    isEscaping ? 'unblock-block--escaping' : '',
  ].filter(Boolean).join(' ');
  
  return (
    <div
      ref={blockRef}
      className={classNames}
      style={{
        position: 'absolute',
        left: pixelPos.left,
        top: pixelPos.top,
        width,
        height,
        '--block-base': block.color,
        cursor: isComplete ? 'default' : (block.orientation === 'H' ? 'ew-resize' : 'ns-resize'),
        transition: isDragging ? 'none' : 'left 0.15s ease, top 0.15s ease',
        touchAction: 'none',
      }}
      onMouseDown={(e) => {
        e.preventDefault();
        handleDragStart(e.clientX, e.clientY);
      }}
      onTouchStart={(e) => {
        const touch = e.touches[0];
        handleDragStart(touch.clientX, touch.clientY);
      }}
    >
      {children}
    </div>
  );
}

export default Block;
